import React, { useEffect, useMemo, useState } from 'react';
import {
  Package, RefreshCw, Search, X, Truck, CheckCircle2, Ban, Undo2,
  Send, Download, MailCheck, ExternalLink, DollarSign,
} from 'lucide-react';
import { api } from '@/lib/api';
import { TextField, Drawer } from '@/components/admin/shared';

const STATUS_TABS = [
  { key: 'all',            label: 'All' },
  { key: 'paid',           label: 'Paid' },
  { key: 'in_production',  label: 'Production' },
  { key: 'shipped',        label: 'Shipped' },
  { key: 'delivered',      label: 'Delivered' },
  { key: 'refunded',       label: 'Refunded' },
  { key: 'cancelled',      label: 'Cancelled' },
  { key: 'failed',         label: 'Failed' },
];

const STATUS_STYLE = {
  pending:       { bg: 'rgba(150,140,120,0.16)', border: 'rgba(194,184,163,0.55)', color: '#c2b8a3' },
  paid:          { bg: 'rgba(25,194,142,0.14)',  border: 'rgba(25,194,142,0.55)',  color: 'var(--pg-emerald-2)' },
  in_production: { bg: 'rgba(226,199,122,0.14)', border: 'rgba(226,199,122,0.55)', color: 'var(--pg-gold-2)' },
  shipped:       { bg: 'rgba(90,168,255,0.14)',  border: 'rgba(90,168,255,0.55)',  color: '#8fbfff' },
  delivered:     { bg: 'rgba(25,194,142,0.12)',  border: 'rgba(25,194,142,0.4)',   color: 'var(--pg-emerald-2)' },
  refunded:      { bg: 'rgba(255,62,165,0.12)',  border: 'rgba(255,62,165,0.5)',   color: 'var(--pg-neon-pink)' },
  cancelled:     { bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.2)',  color: 'var(--pg-ink-muted)' },
  failed:        { bg: 'rgba(255,105,105,0.12)', border: 'rgba(255,105,105,0.5)',  color: '#ff9b9b' },
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-mono tracking-[0.2em] uppercase"
      style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.color }}
      data-testid={`order-status-${status}`}
    >
      <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: s.color }} />
      {status || 'pending'}
    </span>
  );
}

function fmtDate(s) {
  if (!s) return '';
  try {
    return new Date(s).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
}

function fmtMoney(amount, currency = 'usd') {
  const n = Number(amount || 0);
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: (currency || 'usd').toUpperCase() }).format(n);
  } catch {
    return `${(currency || 'USD').toUpperCase()} ${n.toFixed(2)}`;
  }
}

export default function OrdersManager() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ total: 0 });
  const [tab, setTab] = useState('all');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (tab !== 'all') params.status = tab;
      if (q.trim()) params.q = q.trim();
      const [{ data: list }, { data: c }] = await Promise.all([
        api.get('/admin/orders', { params }),
        api.get('/admin/orders/counts'),
      ]);
      setItems(list);
      setCounts(c);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [tab]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q]);

  const openDetail = async (o) => {
    try {
      const { data } = await api.get(`/admin/orders/${o.id}`);
      setDetail(data);
    } catch (e) { setMsg(e?.response?.data?.detail || 'Failed to load order'); }
  };

  const updateStatus = async (newStatus, extras = {}) => {
    if (!detail) return;
    if (!confirm(`Mark order ${detail.id.slice(0, 8)} as ${newStatus.toUpperCase()}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/orders/${detail.id}/status`, { status: newStatus, ...extras });
      setDetail(data);
      setMsg(`Order marked ${newStatus}.`);
      await load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Failed to update status');
    } finally { setBusy(false); }
  };

  const refund = async () => {
    if (!detail) return;
    if (!confirm(`Refund order ${detail.id.slice(0, 8)} via Stripe? This cannot be undone.`)) return;
    const reason = window.prompt('Refund reason (optional):') || '';
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${detail.id}/refund`, { reason });
      setDetail(data.order || detail);
      setMsg('Refund processed via Stripe.');
      await load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Refund failed');
    } finally { setBusy(false); }
  };

  const resendNotification = async (eventKey) => {
    if (!detail) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${detail.id}/resend-notification`, { event_key: eventKey });
      setMsg(data?.sent?.email ? `Notification sent (${eventKey}).` : `Notification: ${data?.skipped_reason || 'template disabled / missing recipient'}.`);
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Resend failed');
    } finally { setBusy(false); }
  };

  const resendDownload = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${detail.id}/resend-download`);
      setMsg(data?.sent?.email ? 'Download link resent via Brevo.' : `Download: ${data?.skipped_reason || 'template missing'}.`);
      await openDetail(detail);
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Resend download failed');
    } finally { setBusy(false); }
  };

  const routePrintful = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${detail.id}/route-printful`);
      setMsg(data?.ok ? `Printful order created: ${data.printful_order_id || 'ok'}` : `Printful: ${data?.error || 'failed'}`);
      await openDetail(detail);
      await load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Printful routing failed');
    } finally { setBusy(false); }
  };

  const tabCounts = useMemo(() => ({
    all: counts.total || 0,
    paid: counts.paid || 0,
    in_production: counts.in_production || 0,
    shipped: counts.shipped || 0,
    delivered: counts.delivered || 0,
    refunded: counts.refunded || 0,
    cancelled: counts.cancelled || 0,
    failed: counts.failed || 0,
  }), [counts]);

  return (
    <div className="space-y-4" data-testid="orders-manager">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Package size={18} className="text-[var(--pg-gold-2)]" />
          <h3 className="font-serif text-lg text-[var(--pg-ink)]">Orders</h3>
          <span className="text-[10px] font-mono tracking-[0.24em] uppercase text-[var(--pg-ink-faint)]">{counts.total || 0} total</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--pg-ink-faint)]" />
            <input
              data-testid="orders-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search email, order id, tracking…"
              className="pg-input pl-8 pr-3 py-2 text-xs rounded-[var(--pg-radius-sm)] w-64 max-w-full"
            />
          </div>
          <button data-testid="orders-refresh" onClick={load} className="pg-btn-ghost px-3 py-2 rounded-[var(--pg-radius-sm)] text-xs flex items-center gap-2">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center flex-wrap gap-1.5" data-testid="orders-status-tabs">
        {STATUS_TABS.map((t) => {
          const active = tab === t.key;
          const s = t.key === 'all'
            ? { bg: 'rgba(255,255,255,0.06)', border: 'rgba(255,255,255,0.25)', color: 'var(--pg-ink)' }
            : STATUS_STYLE[t.key] || STATUS_STYLE.pending;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={`orders-tab-${t.key}`}
              className="px-3 py-1.5 rounded-full border text-[10px] font-mono tracking-[0.24em] uppercase transition-colors"
              style={{
                background: active ? s.bg : 'transparent',
                borderColor: active ? s.border : 'var(--pg-border-2)',
                color: active ? s.color : 'var(--pg-ink-muted)',
              }}
            >
              {t.label}
              {typeof tabCounts[t.key] === 'number' && (
                <span className="ml-1.5 text-[9px] text-[var(--pg-ink-faint)]">{tabCounts[t.key]}</span>
              )}
            </button>
          );
        })}
      </div>

      {msg && (
        <div className="pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-xs text-[var(--pg-ink)] flex justify-between items-center">
          <span>{msg}</span>
          <button onClick={() => setMsg(null)} className="text-[var(--pg-ink-faint)]"><X size={14} /></button>
        </div>
      )}

      {/* List */}
      <div className="pg-admin-card rounded-[var(--pg-radius-md)] overflow-hidden">
        {loading ? (
          <div className="p-6 text-xs font-mono text-[var(--pg-ink-faint)]">Loading orders…</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-xs font-mono text-[var(--pg-ink-faint)]">No orders yet.</div>
        ) : (
          <div className="divide-y divide-[var(--pg-border-2)]">
            {items.map((o) => (
              <button
                key={o.id}
                data-testid={`order-row-${o.id}`}
                onClick={() => openDetail(o)}
                className="w-full text-left p-4 hover:bg-[rgba(242,238,227,0.03)] transition-colors flex items-center gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-serif text-sm text-[var(--pg-ink)] truncate">
                      {(o.items?.[0]?.title) || 'Order'}
                    </p>
                    <StatusPill status={o.status || 'pending'} />
                  </div>
                  <p className="text-[11px] font-mono text-[var(--pg-ink-muted)] mt-1 truncate">
                    {o.customer_email || 'no email'} · #{o.id.slice(0, 8)} · {fmtDate(o.created_at)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-serif text-sm text-[var(--pg-ink)]">{fmtMoney(o.total, o.currency)}</p>
                  <p className="text-[10px] font-mono text-[var(--pg-ink-faint)] uppercase tracking-widest">{o.fulfillment_provider}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Detail Drawer */}
      <Drawer
        open={!!detail}
        onClose={() => setDetail(null)}
        title={detail ? `Order #${detail.id.slice(0, 8)}` : ''}
        onSave={() => setDetail(null)}
        saving={false}
      >
        {detail && (
          <div className="space-y-4" data-testid="order-detail">
            {/* Summary */}
            <div className="pg-input rounded-[var(--pg-radius-sm)] p-3 space-y-2">
              <div className="flex items-center justify-between">
                <StatusPill status={detail.status} />
                <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--pg-ink-faint)]">{detail.fulfillment_provider}</span>
              </div>
              <p className="font-serif text-lg text-[var(--pg-ink)]">{fmtMoney(detail.total, detail.currency)}</p>
              <p className="text-[11px] font-mono text-[var(--pg-ink-muted)]">
                {detail.customer_name || 'Guest'} · {detail.customer_email || 'no email'}
              </p>
              <p className="text-[11px] font-mono text-[var(--pg-ink-faint)]">
                Placed {fmtDate(detail.created_at)}
              </p>
              {detail.session_id && (
                <p className="text-[10px] font-mono text-[var(--pg-ink-faint)] break-all">Stripe: {detail.session_id}</p>
              )}
              {detail.printful_order_id && (
                <p className="text-[10px] font-mono text-[var(--pg-ink-faint)] break-all">Printful: {detail.printful_order_id}</p>
              )}
            </div>

            {/* Items */}
            <div>
              <p className="pg-label text-[10px] mb-2">Items</p>
              <div className="space-y-2">
                {(detail.items || []).map((it, idx) => (
                  <div key={idx} className="pg-input rounded-[var(--pg-radius-sm)] p-3 flex items-center justify-between">
                    <div>
                      <p className="font-serif text-sm text-[var(--pg-ink)]">{it.title}</p>
                      <p className="text-[10px] font-mono text-[var(--pg-ink-faint)]">
                        {it.fulfillment_provider} · qty {it.quantity}
                      </p>
                    </div>
                    <p className="font-mono text-xs text-[var(--pg-ink)]">{fmtMoney(it.unit_price, it.currency)}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Tracking (if any) */}
            {(detail.tracking_number || detail.tracking_url) && (
              <div className="pg-input rounded-[var(--pg-radius-sm)] p-3">
                <p className="pg-label text-[10px]">Tracking</p>
                <p className="font-mono text-xs text-[var(--pg-ink)] mt-1">{detail.tracking_number || '—'}</p>
                {detail.tracking_url && (
                  <a href={detail.tracking_url} target="_blank" rel="noopener noreferrer"
                     className="text-[11px] font-mono text-[var(--pg-gold-2)] hover:underline inline-flex items-center gap-1 mt-1">
                    <ExternalLink size={12} /> Open carrier
                  </a>
                )}
              </div>
            )}

            {/* Digital download (if any) */}
            {detail.metadata?.download_url && (
              <div className="pg-input rounded-[var(--pg-radius-sm)] p-3">
                <p className="pg-label text-[10px]">Digital delivery</p>
                <a href={detail.metadata.download_url} target="_blank" rel="noopener noreferrer"
                   className="text-[11px] font-mono text-[var(--pg-gold-2)] hover:underline inline-flex items-center gap-1 mt-1 break-all">
                  <Download size={12} /> {detail.metadata.download_url}
                </a>
              </div>
            )}

            {/* Timeline */}
            <div>
              <p className="pg-label text-[10px] mb-2">Timeline</p>
              <div className="space-y-1">
                {(detail.events || []).slice().reverse().map((ev, i) => (
                  <div key={i} className="text-[11px] font-mono text-[var(--pg-ink-muted)]">
                    <span className="text-[var(--pg-ink-faint)]">{fmtDate(ev.at)}</span>
                    <span className="mx-2 text-[var(--pg-gold-2)]">{ev.type}</span>
                    <span className="text-[var(--pg-ink)]">{ev.note}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="pt-2 border-t border-[var(--pg-border-2)] space-y-3">
              <p className="pg-label text-[10px]">Lifecycle</p>
              <div className="grid grid-cols-2 gap-2">
                <button data-testid="order-mark-shipped" disabled={busy} onClick={() => updateStatus('shipped', { tracking_number: prompt('Tracking number:') || '', tracking_url: prompt('Tracking URL:') || '' })}
                        className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-3 py-2 text-xs flex items-center gap-2 justify-center">
                  <Truck size={14} /> Mark shipped
                </button>
                <button data-testid="order-mark-delivered" disabled={busy} onClick={() => updateStatus('delivered')}
                        className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-3 py-2 text-xs flex items-center gap-2 justify-center">
                  <CheckCircle2 size={14} /> Mark delivered
                </button>
                <button data-testid="order-cancel" disabled={busy} onClick={() => updateStatus('cancelled')}
                        className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-3 py-2 text-xs flex items-center gap-2 justify-center">
                  <Ban size={14} /> Cancel order
                </button>
                <button data-testid="order-refund" disabled={busy || detail.status === 'refunded'} onClick={refund}
                        className="pg-btn-primary rounded-[var(--pg-radius-sm)] px-3 py-2 text-xs flex items-center gap-2 justify-center disabled:opacity-60">
                  <Undo2 size={14} /> {detail.status === 'refunded' ? 'Refunded' : 'Refund (Stripe)'}
                </button>
              </div>

              <p className="pg-label text-[10px] pt-2">Notifications</p>
              <div className="grid grid-cols-2 gap-2">
                <button data-testid="order-resend-confirmation" disabled={busy} onClick={() => resendNotification('order.confirmed')}
                        className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-3 py-2 text-xs flex items-center gap-2 justify-center">
                  <MailCheck size={14} /> Resend confirmation
                </button>
                <button data-testid="order-resend-shipped" disabled={busy} onClick={() => resendNotification('order.shipped')}
                        className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-3 py-2 text-xs flex items-center gap-2 justify-center">
                  <Send size={14} /> Resend shipped
                </button>
                {detail.fulfillment_provider === 'digital' && (
                  <button data-testid="order-resend-download" disabled={busy} onClick={resendDownload}
                          className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-3 py-2 text-xs flex items-center gap-2 justify-center col-span-2">
                    <Download size={14} /> Regenerate & resend download link
                  </button>
                )}
                {detail.fulfillment_provider === 'printful' && (
                  <button data-testid="order-route-printful" disabled={busy} onClick={routePrintful}
                          className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-3 py-2 text-xs flex items-center gap-2 justify-center col-span-2">
                    <DollarSign size={14} /> Retry Printful fulfillment
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
